import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Input, Select, Button, Spin } from 'antd';
import { UserTag } from '../components/UserTag/UserTag';
import { DefaultAvatar, BandGroupIcon, LockClosedIcon, CogIcon } from '../components/icons';
import UserTypeInfo from '../components/UserTypeInfo/UserTypeInfo';
import type { UserType } from '../components/UserTag/UserTag';
import SpecModal from '../components/SpecModal/SpecModal';
import SpecBottomSheet from '../components/SpecBottomSheet/SpecBottomSheet';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import EditingBar from '../components/EditingBar/EditingBar';
import { radius } from '../theme/tokens/radius';
import { useAuth } from '../contexts/AuthContext';
import { useBand } from '../api/queries';
import { update_band, upload_band_photo } from '../api';
import './EditProfile.css';
import './UserProfile.css';

const GENRE_OPTIONS = [
  'Psychedelic', 'Bluegrass', 'Rock', 'Funk', 'Country',
  'Jazz', 'Blues', 'Hip-Hop', 'R&B', 'Pop', 'Metal',
  'Folk', 'Soul', 'Reggae', 'Indie', 'Electronic',
];

const EditBandProfile: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { user } = useAuth();
  const { data: band, isLoading: bandLoading } = useBand(id);

  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [soundcloudUrl, setSoundcloudUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [genres, setGenres] = useState<string[]>([]);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);

  // Mirrors isAdminPermission() on the backend exactly (permissions.includes('*:*')).
  const isAdmin = !!user?.claims?.permissions?.includes('*:*');
  const isOwner = !!band && !!user && band.claimed_by_user_id === user.uid;
  const isBandAdmin = !!band && !!user && user.claims?.bandRoles?.[band.id]?.role === 'band-admin';
  const canEdit = isOwner || isBandAdmin || isAdmin;

  // Seed the form once the real band loads, without clobbering in-progress
  // edits if the query happens to refetch in the background.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (band && !initializedRef.current) {
      initializedRef.current = true;
      setName(band.name || '');
      setBio(band.bio || band.description || '');
      setSoundcloudUrl(band.soundcloud_url || '');
      setInstagramUrl(band.instagram_url || '');
      setGenres(band.genres || (band.genre ? [band.genre] : []));
      setAvatarUrl(band.photo_url || undefined);
    }
  }, [band]);

  const [photoSheetOpen, setPhotoSheetOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePasscode, setDeletePasscode] = useState('');
  const [openInfoType, setOpenInfoType] = useState<UserType | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    if (!band || saving) return;
    setSaving(true);
    try {
      await update_band(band.id, {
        name,
        bio,
        soundcloud_url: soundcloudUrl,
        instagram_url: instagramUrl,
        genres,
        // Explicit '' (not omitted) so a removed photo actually clears
        // photo_url server-side instead of leaving the old value untouched -
        // update() only merges the keys it's given.
        photo_url: avatarUrl || '',
      });
      navigate(id ? `/bands/${band.id}` : '/band-profile', { state: { profileSaved: true } });
    } catch (error: any) {
      setToastMessage(error.message || 'Failed to save band profile');
      setSaving(false);
    }
  }, [band, saving, name, bio, soundcloudUrl, instagramUrl, genres, avatarUrl, id, navigate]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setPhotoSheetOpen(false);
    if (!file || !band) return;
    setUploadingPhoto(true);
    try {
      const result = await upload_band_photo(band.id, file);
      setAvatarUrl(result.url);
    } catch (error: any) {
      setToastMessage(error.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = () => {
    setAvatarUrl(undefined);
    setPhotoSheetOpen(false);
  };

  if (!user) {
    return (
      <div className="edit-profile-page">
        <div className="user-profile__not-logged-in">
          <div className="user-profile__empty-avatar">
            <DefaultAvatar size={64} />
          </div>
          <h2 className="user-profile__empty-title">Welcome to Spec</h2>
          <p className="user-profile__empty-subtitle">Sign in to edit a band profile.</p>
        </div>
      </div>
    );
  }

  if (!id) {
    return (
      <div className="edit-profile-page">
        <div className="user-profile__not-logged-in">
          <div className="user-profile__empty-avatar">
            <DefaultAvatar size={64} />
          </div>
          <h2 className="user-profile__empty-title">No Band Selected</h2>
          <p className="user-profile__empty-subtitle">Open a band's profile and choose Edit from its menu.</p>
        </div>
      </div>
    );
  }

  if (bandLoading) {
    return (
      <div className="edit-profile-page">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!band) {
    return (
      <div className="edit-profile-page">
        <div className="user-profile__not-logged-in">
          <div className="user-profile__empty-avatar">
            <DefaultAvatar size={64} />
          </div>
          <h2 className="user-profile__empty-title">Band Not Found</h2>
          <p className="user-profile__empty-subtitle">This band doesn't exist or was removed.</p>
        </div>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="edit-profile-page">
        <div className="user-profile__not-logged-in">
          <div className="user-profile__empty-avatar">
            <DefaultAvatar size={64} />
          </div>
          <h2 className="user-profile__empty-title">Not Authorized</h2>
          <p className="user-profile__empty-subtitle">Only the band's owner or a band admin can edit this profile.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-profile-page">
      <EditingBar
        icon={<CogIcon size="m" tone="white" />}
        title="Editing Profile"
        onCancel={() => navigate(-1)}
        onSave={handleSave}
      />
      <div className="edit-profile-content">
        {/* Avatar Section */}
        <div className="edit-profile-avatar-section edit-profile-avatar-section--row">
          <div
            className="edit-profile-avatar edit-profile-avatar--rounded edit-profile-avatar--lg"
            onClick={() => setPhotoSheetOpen(true)}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt={name} />
            ) : (
              <BandGroupIcon size={48} />
            )}
          </div>
          <div className="edit-profile-avatar-info">
            <div className="edit-profile-avatar-name">{name}</div>
            <div className="edit-profile-avatar-badges">
              <UserTag type="band" onClick={() => setOpenInfoType('band')} />
            </div>
          </div>
        </div>
        <Button type="primary" size="small" shape="round" className="edit-profile-update-photo" loading={uploadingPhoto} disabled={uploadingPhoto} onClick={() => setPhotoSheetOpen(true)}>
          Update Photo
        </Button>



        {/* Form */}
        <div className="edit-profile-form">

          {/* Name */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Name</label>
            <Input
              placeholder="Band Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Bio */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Bio</label>
            <Input.TextArea
              placeholder="Tell us about the band..."
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={150}
              showCount
              styles={{
                root: { borderRadius: radius.m, marginBottom: 24 },
                textarea: { borderRadius: radius.m, resize: 'none', height: 80 },
                count: { transform: 'translateY(8px)' },
              }}
            />
          </div>

          {/* SoundCloud */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Song Link</label>
            <Input
              placeholder="Enter song link"
              value={soundcloudUrl}
              onChange={(e) => setSoundcloudUrl(e.target.value)}
            />
          </div>

          {/* Social Links */}
          {/* Website field removed - bandUpdateSchema has no matching
              column; sending it would 400 the whole save. */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Instagram Link</label>
            <Input
              placeholder="Enter Instagram link"
              value={instagramUrl}
              onChange={(e) => setInstagramUrl(e.target.value)}
            />
          </div>
          {/* Genres */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Genres</label>
            <div className="edit-profile-tag-selector">
              <Select
                mode="multiple"
                placeholder="Select genres"
                value={genres}
                onChange={setGenres}
                options={GENRE_OPTIONS.map(g => ({ label: g, value: g }))}
                optionFilterProp="label"
              />
            </div>
          </div>



          {/* Location */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Location</label>
            <Input
              prefix="📍"
              placeholder="City, State"
              value={[band.city, band.state].filter(Boolean).join(', ') || undefined}
              disabled
              suffix={<LockClosedIcon size="s" tone="lightGrey" />}
              className="edit-profile-location-input"
            />
            <span className="edit-profile-location-note">
              *our launch is currently limited to Tulsa
            </span>
          </div>

          {/* Delete Account */}
          <button
            className="edit-profile-danger-link"
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete Account
          </button>
        </div>
      </div>

      {/* Photo Action Sheet */}
      <SpecBottomSheet open={photoSheetOpen} onClose={() => setPhotoSheetOpen(false)}>
        <div className="edit-profile-photo-actions">
          <button
            className="edit-profile-photo-action edit-profile-photo-action--danger"
            onClick={handleRemovePhoto}
          >
            Remove Photo
          </button>
          <button
            className="edit-profile-photo-action edit-profile-photo-action--primary"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload from Photos
          </button>
        </div>
      </SpecBottomSheet>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="edit-profile-file-input"
        onChange={handlePhotoUpload}
      />

      {/* Delete Account Modal */}
      <SpecModal
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        title="Delete Account"
        footer={null}
        closable={false}
        centered
        width={340}
      >
        <p className="edit-profile-modal-body">
          Type the "deletemyaccount" to permanently delete account.
        </p>
        <div className="edit-profile-delete-form">
          <span className="edit-profile-delete-label">Delete Passcode:</span>
          <Input
            placeholder="Enter passcode to delete"
            value={deletePasscode}
            onChange={(e) => setDeletePasscode(e.target.value)}
          />
          <Button
            block
            size="large"
            shape="round"
            className="edit-profile-delete-btn"
            disabled={deletePasscode !== 'deletemyaccount'}
            onClick={() => {
              console.log('Account deleted');
              setDeleteModalOpen(false);
            }}
          >
            DELETE ACCOUNT
          </Button>
        </div>
      </SpecModal>

      <UserTypeInfo type="band" open={openInfoType === 'band'} onClose={() => setOpenInfoType(null)} />

      <SpecAlert message={toastMessage} onDone={() => setToastMessage(null)} />
    </div>
  );
};

export default EditBandProfile;
